import { Box, styled, Tooltip } from "@mui/material";
import { ColumnsPanelTrigger, DataGridPro, DataGridProProps, Toolbar, ToolbarButton } from "@mui/x-data-grid-pro";
import { useContext, useMemo } from "react";
import { AppContext } from "../../../../../../common/contexts/app_context/app_context";
import { useColumnSelectionConfig } from "../../../../../../common/hooks/use_column_selection_config";
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { ValoresContratoFinanceiro } from "../../../../domain/models/valoresContratoFinanceiro";

/**
 * CustomToolbar
 */

const CustomToolbar = ({ valores }: any) => {
    return (
        <div className="w-full">
            <Toolbar>
                <Tooltip title="Colunas">
                    <ColumnsPanelTrigger render={<ToolbarButton />}>
                        <ViewColumnIcon fontSize="small" />
                    </ColumnsPanelTrigger>
                </Tooltip>
            </Toolbar>
            {valores && (
                <div className="flex justify-start pe-[150px] pb-3 m-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-3">
                        <div className="bg-gray-200 px-4 py-3 rounded-xl shadow-sm flex flex-col text-center">
                            <span className="text-sm font-semibold text-gray-700">Valor Financiado Construtora</span>
                            <span className="text-lg font-bold">{toBRL(valores.totalFinanciado)}</span>
                        </div>
                        <div className="bg-gray-200 px-4 py-3 rounded-xl shadow-sm flex flex-col text-center">
                            <span className="text-sm font-semibold text-gray-700">Valor Sem Reajuste Total</span>
                            <span className="text-lg font-bold">{toBRL(valores.totalSemReajuste)}</span>
                        </div>

                        <div className="bg-gray-200 px-4 py-3 rounded-xl shadow-sm flex flex-col text-center">
                            <span className="text-sm font-semibold text-gray-700">Valor Nominal Total</span>
                            <span className="text-lg font-bold">{toBRL(valores.totalNominal)}</span>
                        </div>

                        <div className="bg-gray-200 px-4 py-3 rounded-xl shadow-sm flex flex-col text-center">
                            <span className="text-sm font-semibold text-gray-700">Valor a Receber Total</span>
                            <span className="text-lg font-bold">{toBRL(valores.totalReceber)}</span>
                        </div>

                        <div className="bg-gray-200 px-4 py-3 rounded-xl shadow-sm flex flex-col text-center">
                            <span className="text-sm font-semibold text-gray-700">Valor Juros Total</span>
                            <span className="text-lg font-bold">{toBRL(valores.totalJuros)}</span>
                        </div>

                        <div className="bg-gray-200 px-4 py-3 rounded-xl shadow-sm flex flex-col text-center">
                            <span className="text-sm font-semibold text-gray-700">Valor Final Total</span>
                            <span className="text-lg font-bold">{toBRL(valores.totalFinal)}</span>
                        </div>

                        <div className="bg-green-200 px-4 py-3 rounded-xl shadow-sm flex flex-col text-center">
                            <span className="text-sm font-semibold text-green-700">Valor Total Recebido</span>
                            <span className="text-lg font-bold text-green-800">{toBRL(valores.totalRecebido)}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const toBRL = (value?: number) => {
    return (
        value?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) ?? "R$ 0,00"
    );
}

interface Props extends DataGridProProps {
    tableName: string;
    height?: string;
    textSize?: "small" | "medium";
    valoresContratoFinanceiro?: ValoresContratoFinanceiro;
}

export const PgtoStyledDataGrid = ({ textSize, tableName, valoresContratoFinanceiro, disableRowSelectionExcludeModel = true, height = "80vh", ...props }: Props) => {
    const [
        columnsVisibilityModel,
        handleColumnsVisibilityModel] = useColumnSelectionConfig({
            tabela: tableName,
        });

    const mode = useContext(AppContext).theme
    const StyledDataGrid = useMemo(() => {
        return styled(DataGridPro)(() => ({
            '& .MuiDataGrid-footerContainer': {
                display: 'none',
            },
            "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#aeeece",
                fontSize: '0.75rem',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
                wordBreak: 'normal',
                whiteSpace: 'normal',
                lineHeight: 'auto',
                color: 'black',
                fontSize: '0.75rem',
            },
            "& .MuiDataGrid-cell": {
                fontSize: '0.75rem',
            },
            backgroundColor: mode === "dark" ? "#2C2C2C" : "#FFFFFF",
        }))
    }, [mode]);

    return (
        <Box sx={{ height: height, width: '100%' }}>
            <StyledDataGrid
                showToolbar={true}
                slots={{
                    toolbar: CustomToolbar,
                }}
                slotProps={{
                    toolbar: {
                        valores: valoresContratoFinanceiro,
                    } as any,
                    columnsPanel: {
                        style: {
                            color: mode === "dark" ? '#ffffff' : "#000000",
                        }
                    },
                }}
                columnVisibilityModel={columnsVisibilityModel}
                disableColumnSorting={true}
                multipleColumnsSortingMode='always'
                disableRowSelectionExcludeModel={disableRowSelectionExcludeModel}
                onColumnVisibilityModelChange={handleColumnsVisibilityModel}
                {...props}
                disableColumnMenu={false}
                localeText={{
                    noRowsLabel: "Não existem itens para mostrar!",

                    columnsManagementSearchTitle: 'Buscar',
                    columnsManagementNoColumns: 'Não há colunas',
                    columnsManagementShowHideAllText: 'Exibir/Esconder',
                    columnsManagementReset: 'Reiniciar',
                    columnsManagementDeleteIconLabel: 'Limpar',

                    pinToLeft: 'Fixar a esquerda',
                    pinToRight: 'Fixar a direita',
                    unpin: 'Desfixar',

                    columnMenuShowColumns: 'Exibir colunas',
                    columnMenuManageColumns: 'Gerenciar colunas',

                    columnMenuHideColumn: 'Esconder coluna',
                    columnMenuUnsort: 'Desordenar',
                    columnMenuSortAsc: 'Ordenar ASC',
                    columnMenuSortDesc: 'Ordenar DESC',
                }}
            />
        </Box>
    );
}